/*=============================================== HeaderPage ===============================================*/

import { Link } from "react-router-dom"
import { Text } from "tsx-library-julseb"

import { ComponentsLayout } from "components"
import type { ItemType, PropsItemType } from "components"

import {
    commonProps,
    propTo,
    propHref,
    inputBackgroundColor,
    inputVariants,
    iconBoxicon,
} from "data"

const HeaderPage = () => {
    const items: ItemType[] = [
        {
            content: (
                <Text>
                    <Link
                        to="/components/header/demo"
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        Demo here.
                    </Link>
                </Text>
            ),
            code: 'const links = [\n    {\n        text: "Link",\n        to: "#",\n    },\n    {\n        text: "Link",\n        to: "/",\n    },\n    {\n        text: "Link",\n        to: "/",\n    },\n    {\n        text: "Button",\n        onClick: () => alert("Clicked"),\n    },\n]\n\n<Header\n    logo={{ text: "Logo" }}\n    position="fixed"\n    hideOnScroll\n    navMobileVariant="drawer"\n    search={{\n        pathname: "",\n        icon: "search",\n        placeholder: "Search here",\n        keyboardShortcut: ["Command", "KeyK"],\n        maxWidth: 300,\n        backgroundColor: "light",\n        showKeys: true,\n    }}\n>\n    {links.map(({ text, to, onClick }, i) =>\n        to ? (\n            <NavLink to={to} key={i}>\n                {text}\n            </NavLink>\n        ) : (\n            <button onClick={onClick} key={i}>\n                {text}\n            </button>\n        )\n    )}\n</Header>',
            codeTs: 'type LinkType = {\n    text: string\n    to?: string\n    onClick?: () => void\n}\n\nconst links: LinkType[] = [\n    {\n        text: "Link",\n        to: "#",\n    },\n    {\n        text: "Link",\n        to: "/",\n    },\n    {\n        text: "Link",\n        to: "/",\n    },\n    {\n        text: "Button",\n        onClick: () => alert("Clicked"),\n    },\n]\n\n<Header\n    logo={{ text: "Logo" }}\n    position="fixed"\n    hideOnScroll\n    navMobileVariant="drawer"\n    search={{\n        pathname: "",\n        icon: "search",\n        placeholder: "Search here",\n        keyboardShortcut: ["Command", "KeyK"],\n        maxWidth: 300,\n        backgroundColor: "light",\n        showKeys: true,\n    }}\n>\n    {links.map(({ text, to, onClick }, i) =>\n        to ? (\n            <NavLink to={to} key={i}>\n                {text}\n            </NavLink>\n        ) : (\n            <button onClick={onClick} key={i}>\n                {text}\n            </button>\n        )\n    )}\n</Header>',
        },
    ]

    const propsItems: PropsItemType[] = [
        {
            subtitle: "position",
            type: "string",
            possible: ["relative", "absolute", "fixed"],
            default: "relative",
        },
        {
            subtitle: "hideOnScroll",
            intro: (
                <>
                    Only if <code>position</code> is set to <code>fixed</code>.
                    This hides the header when user scrolls on the page. If you
                    use it as a <code>boolean</code>, the default value is{" "}
                    <code>400</code>.
                </>
            ),
            type: "number or boolean",
            possible: ["true", "false", "any number"],
        },
        {
            subtitle: "variant",
            type: "string",
            possible: ["primary", "white", "transparent"],
            default: "primary",
        },
        {
            subtitle: "burgerPosition",
            type: "string",
            possible: ["left", "right"],
            default: "right",
        },
        {
            subtitle: "navDesktopVariant",
            type: "string",
            possible: ["left", "right"],
            default: "right",
        },
        {
            subtitle: "navMobileVariant",
            type: "string",
            possible: ["full", "top", "drawer"],
            default: "top",
        },
        "separator",
        {
            subtitle: "logo",
            type: "object",
            required: true,
        },
        {
            title: (
                <>
                    <code>logo</code> object
                </>
            ),
            subtitle: "text",
            intro: (
                <>
                    Either <code>text</code> or <code>img</code> are required.
                </>
            ),
            type: "string",
        },
        {
            subtitle: "img",
            intro: (
                <>
                    Either <code>text</code> or <code>img</code> are required.
                </>
            ),
            type: "string",
        },
        {
            subtitle: "alt",
            intro: (
                <>
                    Only if <code>img</code> is defined.
                </>
            ),
            type: "string",
            default: "Logo",
        },
        {
            subtitle: "width",
            intro: (
                <>
                    Only if <code>img</code> is defined.
                </>
            ),
            type: "number",
            default: 100,
        },
        {
            subtitle: "height",
            intro: (
                <>
                    Only if <code>img</code> is defined.
                </>
            ),
            type: "number",
            default: 30,
        },
        propTo,
        propHref,
        "separator",
        {
            subtitle: "search",
            intro: (
                <>
                    {commonProps.routerOnly} Here, we are using{" "}
                    <a
                        href="https://reactrouter.com/en/main/utils/create-search-params"
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        createSearchParams
                    </a>{" "}
                    and{" "}
                    <a
                        href="https://reactrouter.com/en/main/hooks/use-navigate"
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        useNavigate
                    </a>{" "}
                    hooks to handle the search.
                </>
            ),
            type: "object",
        },
        {
            title: (
                <>
                    <code>search</code> object
                </>
            ),
            subtitle: "pathname",
            type: "string",
            required: true,
        },
        {
            subtitle: "queries",
            type: "array of array of strings",
        },
        {
            subtitle: "icon",
            intro: commonProps.addIconCapital,
            type: "string",
        },
        {
            subtitle: "iconSize",
            type: "number",
            default: 24,
        },
        {
            subtitle: "iconClear",
            type: "string",
            default: iconBoxicon("x"),
        },
        {
            subtitle: "placeholder",
            type: "string",
        },
        {
            subtitle: "keyboardShortcut",
            intro: "Adds a keyboard shortcut to focus the input.",
            type: "array of strings",
        },
        {
            subtitle: "showKeys",
            intro: (
                <>
                    Only if <code>keyboardShortcut</code> is defined.
                </>
            ),
            type: "boolean",
        },
        inputBackgroundColor(),
        inputVariants(),
        {
            subtitle: "maxWidth",
            type: "string or number",
            default: 400,
        },
    ]

    return <ComponentsLayout items={items} propsItems={propsItems} />
}

export default HeaderPage
